import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import {
  getClientDashboard,
  getClientProjects,
  getClientProjectTasks,
  getClientTeam,
  getClientHours,
  getClientFinancials,
  getClientActivity,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import {
  ArrowLeft,
  Buildings,
  ChartLine,
  Users,
  Clock,
  Receipt,
  CurrencyDollar,
  Pulse,
  Folder,
  ListChecks,
  TrendUp,
  TrendDown,
  Warning,
  CheckCircle,
  Eye,
  CalendarBlank,
  Envelope,
  Phone,
  MapPin,
  IdentificationCard,
  Star,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// Stat Card Component
function StatCard({ icon: Icon, label, value, subValue, color = 'cyan', trend }) {
  const colorClasses = {
    cyan: 'from-[#3AB8D6]/20 to-[#3AB8D6]/5 border-[#3AB8D6]/30',
    gold: 'from-[#E2B05E]/20 to-[#E2B05E]/5 border-[#E2B05E]/30',
    green: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/30',
    red: 'from-red-500/20 to-red-500/5 border-red-500/30',
    purple: 'from-purple-500/20 to-purple-500/5 border-purple-500/30',
  };
  
  return (
    <div className={`valhalla-card p-4 bg-gradient-to-br ${colorClasses[color]} border`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5 text-[#9CA3AF]" />
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {trend >= 0 ? <TrendUp className="w-3 h-3" /> : <TrendDown className="w-3 h-3" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-[#F3F4F6]">{value}</p>
      <p className="text-xs text-[#9CA3AF] mt-1">{label}</p>
      {subValue && <p className="text-xs text-[#6B7280] mt-0.5">{subValue}</p>}
    </div>
  );
}

// Overview Tab Component
function OverviewTab({ dashboard, loading }) {
  const { t, isSpanish } = useLanguage();
  
  if (loading || !dashboard) {
    return <div className="text-center py-8 text-[#9CA3AF]">{t('loading')}</div>;
  }
  
  const { client, summary, financials, time, team } = dashboard;
  
  return (
    <div className="space-y-6">
      {/* Client Info Header */}
      <div className="valhalla-card p-6">
        <div className="flex items-start gap-6">
          <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-[#E2B05E] to-[#F0C47A] flex items-center justify-center text-[#0B0E14] font-bold text-3xl">
            {client.name?.charAt(0)?.toUpperCase()}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold text-[#F3F4F6]">{client.name}</h2>
              <Badge className={client.is_active !== false ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'}>
                {client.is_active !== false ? t('active') : t('inactive')}
              </Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-[#9CA3AF]">
              {client.contact_name && (
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  <span>{client.contact_name}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Envelope className="w-4 h-4" />
                <span>{client.email}</span>
              </div>
              {client.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>{client.phone}</span>
                </div>
              )}
              {client.tax_id && (
                <div className="flex items-center gap-2">
                  <IdentificationCard className="w-4 h-4" />
                  <span>{client.tax_id}</span>
                </div>
              )}
            </div>
            {client.address && (
              <div className="flex items-center gap-2 mt-2 text-sm text-[#9CA3AF]">
                <MapPin className="w-4 h-4" />
                <span>{client.address}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard
          icon={Folder}
          label={isSpanish ? "Total Proyectos" : "Total Projects"}
          value={summary.total_projects}
          color="cyan"
        />
        <StatCard
          icon={Pulse}
          label={isSpanish ? "Proyectos Activos" : "Active Projects"}
          value={summary.active_projects}
          color="green"
        />
        <StatCard
          icon={CheckCircle}
          label={isSpanish ? "Completados" : "Completed"}
          value={summary.completed_projects}
          color="gold"
        />
        <StatCard
          icon={Warning}
          label={isSpanish ? "Retrasados" : "Delayed"}
          value={summary.delayed_projects}
          color={summary.delayed_projects > 0 ? "red" : "cyan"}
        />
        <StatCard
          icon={Clock}
          label={isSpanish ? "Horas Totales" : "Total Hours"}
          value={time.total_hours?.toFixed(1) || 0}
          subValue={`${time.billable_hours?.toFixed(1) || 0} ${isSpanish ? 'facturables' : 'billable'}`}
          color="purple"
        />
        <StatCard
          icon={Users}
          label={isSpanish ? "Equipo" : "Team Size"}
          value={team.employees_assigned}
          subValue={team.pm_in_charge ? `PM: ${team.pm_in_charge}` : null}
          color="cyan"
        />
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="valhalla-card p-6">
          <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4 flex items-center gap-2">
            <CurrencyDollar className="w-5 h-5 text-[#E2B05E]" />
            {isSpanish ? 'Resumen Financiero' : 'Financial Summary'}
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-[#242E42]">
              <span className="text-[#9CA3AF]">{isSpanish ? 'Total Facturado' : 'Total Invoiced'}</span>
              <span className="text-[#F3F4F6] font-semibold">${financials.total_invoiced?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-[#242E42]">
              <span className="text-[#9CA3AF]">{isSpanish ? 'Total Cobrado' : 'Total Collected'}</span>
              <span className="text-emerald-400 font-semibold">${financials.total_paid?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-[#242E42]">
              <span className="text-[#9CA3AF]">{isSpanish ? 'Saldo Pendiente' : 'Outstanding Balance'}</span>
              <span className={`font-semibold ${financials.outstanding_balance > 0 ? 'text-amber-400' : 'text-[#F3F4F6]'}`}>
                ${financials.outstanding_balance?.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-[#242E42]">
              <span className="text-[#9CA3AF]">{isSpanish ? 'Promedio Mensual' : 'Avg Monthly Billing'}</span>
              <span className="text-[#F3F4F6] font-semibold">${financials.avg_monthly_billing?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-[#9CA3AF]">{isSpanish ? 'Costo Total' : 'Total Cost'}</span>
              <span className="text-[#F3F4F6] font-semibold">${financials.total_cost?.toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="valhalla-card p-6">
          <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4 flex items-center gap-2">
            <ChartLine className="w-5 h-5 text-[#3AB8D6]" />
            {isSpanish ? 'Rentabilidad' : 'Profitability'}
          </h3>
          <div className="flex flex-col items-center justify-center h-40">
            <div className={`text-4xl font-bold ${financials.profitability >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              ${financials.profitability?.toLocaleString()}
            </div>
            <div className="text-[#9CA3AF] mt-2">{isSpanish ? 'Ganancia Neta' : 'Net Profit'}</div>
            <div className={`text-2xl font-semibold mt-4 ${financials.profit_margin >= 20 ? 'text-emerald-400' : financials.profit_margin >= 0 ? 'text-amber-400' : 'text-red-400'}`}>
              {financials.profit_margin}%
            </div>
            <div className="text-[#9CA3AF] text-sm">{isSpanish ? 'Margen de Ganancia' : 'Profit Margin'}</div>
          </div>
        </div>
      </div>

      {/* Top Contributors */}
      {team.top_contributors && team.top_contributors.length > 0 && (
        <div className="valhalla-card p-6">
          <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-[#E2B05E]" />
            {isSpanish ? 'Top Contribuidores' : 'Top Contributors'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {team.top_contributors.map((contributor, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-[#0B0E14] rounded-lg">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3AB8D6] to-[#2A8FA8] flex items-center justify-center text-white font-semibold text-sm">
                  {idx + 1}
                </div>
                <div>
                  <p className="text-[#F3F4F6] font-medium text-sm">{contributor.name || 'N/A'}</p>
                  <p className="text-[#9CA3AF] text-xs">{contributor.hours?.toFixed(1)} hrs</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Projects Tab Component
function ProjectsTab({ clientId }) {
  const { t, isSpanish } = useLanguage();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await getClientProjects(clientId, params);
      setProjects(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [clientId, statusFilter, t]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleProjectClick = async (project) => {
    setSelectedProject(project);
    setLoadingTasks(true);
    try {
      const response = await getClientProjectTasks(clientId, project._id);
      setTasks(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoadingTasks(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      planeacion: 'bg-blue-500/20 text-blue-400',
      activo: 'bg-emerald-500/20 text-emerald-400',
      en_pausa: 'bg-amber-500/20 text-amber-400',
      cerrado: 'bg-gray-500/20 text-gray-400',
      cancelado: 'bg-red-500/20 text-red-400',
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  if (selectedProject) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => setSelectedProject(null)} className="text-[#9CA3AF] hover:text-[#F3F4F6]">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {isSpanish ? 'Volver a proyectos' : 'Back to projects'}
        </Button>

        {/* Project Detail Header */}
        <div className="valhalla-card p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-[#F3F4F6]">{selectedProject.name}</h3>
              <p className="text-[#9CA3AF] mt-1">{selectedProject.description}</p>
            </div>
            <Badge className={getStatusColor(selectedProject.status)}>{selectedProject.status}</Badge>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="text-center p-3 bg-[#0B0E14] rounded-lg">
              <p className="text-2xl font-bold text-[#3AB8D6]">{selectedProject.stats?.progress?.toFixed(0)}%</p>
              <p className="text-xs text-[#9CA3AF]">{isSpanish ? 'Progreso' : 'Progress'}</p>
            </div>
            <div className="text-center p-3 bg-[#0B0E14] rounded-lg">
              <p className="text-2xl font-bold text-[#E2B05E]">{selectedProject.stats?.hours_consumed?.toFixed(1)}</p>
              <p className="text-xs text-[#9CA3AF]">{isSpanish ? 'Horas' : 'Hours'}</p>
            </div>
            <div className="text-center p-3 bg-[#0B0E14] rounded-lg">
              <p className="text-2xl font-bold text-emerald-400">${selectedProject.stats?.paid_amount?.toLocaleString()}</p>
              <p className="text-xs text-[#9CA3AF]">{isSpanish ? 'Pagado' : 'Paid'}</p>
            </div>
            <div className="text-center p-3 bg-[#0B0E14] rounded-lg">
              <p className={`text-2xl font-bold ${selectedProject.stats?.margin >= 20 ? 'text-emerald-400' : selectedProject.stats?.margin >= 0 ? 'text-amber-400' : 'text-red-400'}`}>
                {selectedProject.stats?.margin}%
              </p>
              <p className="text-xs text-[#9CA3AF]">{isSpanish ? 'Margen' : 'Margin'}</p>
            </div>
          </div>
        </div>

        {/* Tasks List */}
        <div className="valhalla-card p-6">
          <h4 className="text-lg font-semibold text-[#F3F4F6] mb-4 flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-[#3AB8D6]" />
            {isSpanish ? 'Tareas del Proyecto' : 'Project Tasks'} ({tasks.length})
          </h4>
          
          {loadingTasks ? (
            <div className="text-center py-8 text-[#9CA3AF]">{t('loading')}</div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-8 text-[#9CA3AF]">{t('noData')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#242E42]">
                    <th className="text-left py-3 px-4 text-[#9CA3AF] font-medium text-sm">{isSpanish ? 'Tarea' : 'Task'}</th>
                    <th className="text-left py-3 px-4 text-[#9CA3AF] font-medium text-sm">{t('status')}</th>
                    <th className="text-left py-3 px-4 text-[#9CA3AF] font-medium text-sm">{isSpanish ? 'Asignado' : 'Assignee'}</th>
                    <th className="text-left py-3 px-4 text-[#9CA3AF] font-medium text-sm">{t('priority')}</th>
                    <th className="text-right py-3 px-4 text-[#9CA3AF] font-medium text-sm">{isSpanish ? 'Est.' : 'Est.'}</th>
                    <th className="text-right py-3 px-4 text-[#9CA3AF] font-medium text-sm">{isSpanish ? 'Real' : 'Actual'}</th>
                    <th className="text-right py-3 px-4 text-[#9CA3AF] font-medium text-sm">{isSpanish ? 'Desv.' : 'Dev.'}</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task) => (
                    <tr key={task._id} className="border-b border-[#242E42]/50 hover:bg-[#0B0E14]/50">
                      <td className="py-3 px-4">
                        <p className="text-[#F3F4F6] font-medium">{task.title}</p>
                        <p className="text-xs text-[#9CA3AF]">{task.complexity} • {task.story_points} pts</p>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={getStatusColor(task.status)}>{task.status}</Badge>
                      </td>
                      <td className="py-3 px-4 text-[#9CA3AF]">{task.assignee_name || '-'}</td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className={`border-${task.priority === 'critica' ? 'red' : task.priority === 'alta' ? 'amber' : 'gray'}-500/50`}>
                          {task.priority}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right text-[#9CA3AF]">{task.estimated_hours}h</td>
                      <td className="py-3 px-4 text-right text-[#F3F4F6]">{task.logged_hours?.toFixed(1)}h</td>
                      <td className={`py-3 px-4 text-right ${task.hours_deviation > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                        {task.hours_deviation > 0 ? '+' : ''}{task.hours_deviation?.toFixed(1)}h
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 valhalla-input">
            <SelectValue placeholder={t('status')} />
          </SelectTrigger>
          <SelectContent className="bg-[#151B26] border-[#242E42]">
            <SelectItem value="all">{t('all')}</SelectItem>
            <SelectItem value="planeacion">{t('planning')}</SelectItem>
            <SelectItem value="activo">{t('active')}</SelectItem>
            <SelectItem value="en_pausa">{t('paused')}</SelectItem>
            <SelectItem value="cerrado">{t('closed')}</SelectItem>
            <SelectItem value="cancelado">{t('cancelled')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Projects Table */}
      {loading ? (
        <div className="text-center py-8 text-[#9CA3AF]">{t('loading')}</div>
      ) : projects.length === 0 ? (
        <div className="valhalla-card p-12 text-center">
          <Folder className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" />
          <p className="text-[#9CA3AF]">{t('noData')}</p>
        </div>
      ) : (
        <div className="valhalla-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#0B0E14]">
              <tr>
                <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Proyecto' : 'Project'}</th>
                <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{t('status')}</th>
                <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'PM' : 'PM'}</th>
                <th className="text-right py-4 px-6 text-[#9CA3AF] font-medium">{t('progress')}</th>
                <th className="text-right py-4 px-6 text-[#9CA3AF] font-medium">{t('hours')}</th>
                <th className="text-right py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Facturado' : 'Billed'}</th>
                <th className="text-right py-4 px-6 text-[#9CA3AF] font-medium">{t('margin')}</th>
                <th className="text-center py-4 px-6 text-[#9CA3AF] font-medium">{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr key={project._id} className="border-t border-[#242E42] hover:bg-[#0B0E14]/50">
                  <td className="py-4 px-6">
                    <p className="text-[#F3F4F6] font-medium">{project.name}</p>
                    <p className="text-xs text-[#9CA3AF]">{project.start_date} - {project.end_date || '...'}</p>
                  </td>
                  <td className="py-4 px-6">
                    <Badge className={getStatusColor(project.status)}>{project.status}</Badge>
                  </td>
                  <td className="py-4 px-6 text-[#9CA3AF]">{project.stats?.pm_name || '-'}</td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2 justify-end">
                      <Progress value={project.stats?.progress || 0} className="w-20 h-2" />
                      <span className="text-[#F3F4F6] text-sm w-12 text-right">{project.stats?.progress?.toFixed(0)}%</span>
                    </div>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <span className="text-[#F3F4F6]">{project.stats?.hours_consumed?.toFixed(1)}</span>
                    <span className="text-[#9CA3AF]">/{project.stats?.hours_estimated || 0}</span>
                  </td>
                  <td className="py-4 px-6 text-right text-[#F3F4F6]">${project.stats?.billed_amount?.toLocaleString()}</td>
                  <td className={`py-4 px-6 text-right font-semibold ${project.stats?.margin >= 20 ? 'text-emerald-400' : project.stats?.margin >= 0 ? 'text-amber-400' : 'text-red-400'}`}>
                    {project.stats?.margin}%
                  </td>
                  <td className="py-4 px-6 text-center">
                    <Button size="sm" variant="ghost" onClick={() => handleProjectClick(project)} className="text-[#3AB8D6] hover:text-[#F3F4F6]">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Team Tab Component
function TeamTab({ clientId }) {
  const { t, isSpanish } = useLanguage();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTeam = useCallback(async () => {
    try {
      const response = await getClientTeam(clientId);
      setTeam(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [clientId, t]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  if (loading) {
    return <div className="text-center py-8 text-[#9CA3AF]">{t('loading')}</div>;
  }

  if (team.length === 0) {
    return (
      <div className="valhalla-card p-12 text-center">
        <Users className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" />
        <p className="text-[#9CA3AF]">{t('noData')}</p>
      </div>
    );
  }

  return (
    <div className="valhalla-card overflow-hidden">
      <table className="w-full">
        <thead className="bg-[#0B0E14]">
          <tr>
            <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Empleado' : 'Employee'}</th>
            <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{t('position')}</th>
            <th className="text-center py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Tareas' : 'Tasks'}</th>
            <th className="text-right py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Horas Total' : 'Total Hours'}</th>
            <th className="text-right py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Facturables' : 'Billable'}</th>
            <th className="text-right py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Costo' : 'Cost'}</th>
            <th className="text-right py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Facturado' : 'Billed'}</th>
            <th className="text-right py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Eficiencia' : 'Efficiency'}</th>
            <th className="text-right py-4 px-6 text-[#9CA3AF] font-medium">{t('utilization')}</th>
          </tr>
        </thead>
        <tbody>
          {team.map((member) => (
            <tr key={member.employee_id} className="border-t border-[#242E42] hover:bg-[#0B0E14]/50">
              <td className="py-4 px-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3AB8D6] to-[#2A8FA8] flex items-center justify-center text-white font-semibold">
                    {member.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div>
                    <p className="text-[#F3F4F6] font-medium">{member.name}</p>
                    <p className="text-xs text-[#9CA3AF]">{member.level}</p>
                  </div>
                </div>
              </td>
              <td className="py-4 px-6 text-[#9CA3AF]">{member.position}</td>
              <td className="py-4 px-6 text-center">
                <span className="text-emerald-400">{member.tasks_completed}</span>
                <span className="text-[#9CA3AF]">/{member.tasks_assigned}</span>
              </td>
              <td className="py-4 px-6 text-right text-[#F3F4F6]">{member.total_hours?.toFixed(1)}h</td>
              <td className="py-4 px-6 text-right text-[#3AB8D6]">{member.billable_hours?.toFixed(1)}h</td>
              <td className="py-4 px-6 text-right text-[#9CA3AF]">${member.internal_cost?.toLocaleString()}</td>
              <td className="py-4 px-6 text-right text-[#E2B05E]">${member.billed_value?.toLocaleString()}</td>
              <td className={`py-4 px-6 text-right font-semibold ${member.efficiency_ratio >= 80 ? 'text-emerald-400' : member.efficiency_ratio >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                {member.efficiency_ratio}%
              </td>
              <td className={`py-4 px-6 text-right font-semibold ${member.utilization_pct >= 80 ? 'text-emerald-400' : member.utilization_pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                {member.utilization_pct}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Hours Tab Component
function HoursTab({ clientId }) {
  const { t, isSpanish } = useLanguage();
  const [data, setData] = useState({ entries: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchHours = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const response = await getClientHours(clientId, params);
      setData(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [clientId, dateFrom, dateTo, t]);

  useEffect(() => {
    fetchHours();
  }, [fetchHours]);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4 items-end">
        <div className="space-y-2">
          <label className="text-[#9CA3AF] text-sm">{isSpanish ? 'Desde' : 'From'}</label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="valhalla-input w-40" />
        </div>
        <div className="space-y-2">
          <label className="text-[#9CA3AF] text-sm">{isSpanish ? 'Hasta' : 'To'}</label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="valhalla-input w-40" />
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <StatCard icon={Clock} label={isSpanish ? 'Horas Totales' : 'Total Hours'} value={data.summary?.total_hours?.toFixed(1) || 0} color="cyan" />
        <StatCard icon={CurrencyDollar} label={isSpanish ? 'Horas Facturables' : 'Billable Hours'} value={data.summary?.billable_hours?.toFixed(1) || 0} color="green" />
        <StatCard icon={Clock} label={isSpanish ? 'No Facturables' : 'Non-Billable'} value={data.summary?.non_billable_hours?.toFixed(1) || 0} color="gold" />
      </div>

      {/* Entries Table */}
      {loading ? (
        <div className="text-center py-8 text-[#9CA3AF]">{t('loading')}</div>
      ) : data.entries.length === 0 ? (
        <div className="valhalla-card p-12 text-center">
          <Clock className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" />
          <p className="text-[#9CA3AF]">{t('noData')}</p>
        </div>
      ) : (
        <div className="valhalla-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#0B0E14]">
              <tr>
                <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{t('date')}</th>
                <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Empleado' : 'Employee'}</th>
                <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Proyecto' : 'Project'}</th>
                <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{t('description')}</th>
                <th className="text-right py-4 px-6 text-[#9CA3AF] font-medium">{t('hours')}</th>
                <th className="text-center py-4 px-6 text-[#9CA3AF] font-medium">{t('billable')}</th>
                <th className="text-center py-4 px-6 text-[#9CA3AF] font-medium">{t('status')}</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((entry) => (
                <tr key={entry._id} className="border-t border-[#242E42] hover:bg-[#0B0E14]/50">
                  <td className="py-4 px-6 text-[#F3F4F6]">{entry.date}</td>
                  <td className="py-4 px-6 text-[#9CA3AF]">{entry.employee_name || '-'}</td>
                  <td className="py-4 px-6 text-[#9CA3AF]">{entry.project_name || '-'}</td>
                  <td className="py-4 px-6 text-[#9CA3AF] max-w-xs truncate">{entry.description}</td>
                  <td className="py-4 px-6 text-right text-[#F3F4F6] font-medium">{entry.hours}h</td>
                  <td className="py-4 px-6 text-center">
                    <Badge className={entry.is_billable ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'}>
                      {entry.is_billable ? t('billable') : t('nonBillable')}
                    </Badge>
                  </td>
                  <td className="py-4 px-6 text-center">
                    <Badge className={entry.status === 'aprobada' ? 'bg-emerald-500/20 text-emerald-400' : entry.status === 'rechazada' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}>
                      {entry.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Financials Tab Component
function FinancialsTab({ clientId }) {
  const { t, isSpanish } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());

  const fetchFinancials = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getClientFinancials(clientId, { year });
      setData(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [clientId, year, t]);

  useEffect(() => {
    fetchFinancials();
  }, [fetchFinancials]);

  if (loading || !data) {
    return <div className="text-center py-8 text-[#9CA3AF]">{t('loading')}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Year Selector */}
      <div className="flex gap-4 items-center">
        <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
          <SelectTrigger className="w-32 valhalla-input">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#151B26] border-[#242E42]">
            {[2024, 2025, 2026].map(y => (
              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-[#9CA3AF]">
          {isSpanish ? 'Total Facturado:' : 'Total Invoiced:'} <span className="text-[#E2B05E] font-semibold">${data.totals?.invoiced?.toLocaleString()}</span>
          {' | '}
          {isSpanish ? 'Cobrado:' : 'Collected:'} <span className="text-emerald-400 font-semibold">${data.totals?.paid?.toLocaleString()}</span>
        </div>
      </div>

      {/* Monthly Chart */}
      <div className="valhalla-card p-6">
        <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4">{isSpanish ? 'Facturación Mensual' : 'Monthly Billing'}</h3>
        <div className="flex items-end gap-2 h-48">
          {data.monthly_breakdown?.map((month, idx) => {
            const maxValue = Math.max(...data.monthly_breakdown.map(m => Math.max(m.invoiced, m.paid))) || 1;
            const invoicedHeight = (month.invoiced / maxValue) * 100;
            const paidHeight = (month.paid / maxValue) * 100;
            return (
              <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full flex gap-1 items-end h-36">
                  <div className="flex-1 bg-[#E2B05E]/50 rounded-t" style={{ height: `${invoicedHeight}%` }} title={`${isSpanish ? 'Facturado' : 'Invoiced'}: $${month.invoiced}`}></div>
                  <div className="flex-1 bg-emerald-500/50 rounded-t" style={{ height: `${paidHeight}%` }} title={`${isSpanish ? 'Cobrado' : 'Paid'}: $${month.paid}`}></div>
                </div>
                <span className="text-xs text-[#9CA3AF]">{month.month_name}</span>
              </div>
            );
          })}
        </div>
        <div className="flex justify-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-[#E2B05E]/50 rounded"></div>
            <span className="text-xs text-[#9CA3AF]">{isSpanish ? 'Facturado' : 'Invoiced'}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-emerald-500/50 rounded"></div>
            <span className="text-xs text-[#9CA3AF]">{isSpanish ? 'Cobrado' : 'Paid'}</span>
          </div>
        </div>
      </div>

      {/* Invoices List */}
      <div className="valhalla-card p-6">
        <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4 flex items-center gap-2">
          <Receipt className="w-5 h-5 text-[#3AB8D6]" />
          {t('invoices')} ({data.invoices?.length || 0})
        </h3>
        {data.invoices?.length === 0 ? (
          <p className="text-[#9CA3AF] text-center py-4">{t('noData')}</p>
        ) : (
          <div className="space-y-2">
            {data.invoices?.slice(0, 10).map((inv) => (
              <div key={inv._id} className="flex items-center justify-between py-3 px-4 bg-[#0B0E14] rounded-lg">
                <div>
                  <p className="text-[#F3F4F6] font-medium">{inv.invoice_number}</p>
                  <p className="text-xs text-[#9CA3AF]">{inv.issue_date}</p>
                </div>
                <div className="text-right">
                  <p className="text-[#F3F4F6] font-semibold">${inv.total?.toLocaleString()}</p>
                  <Badge className={inv.status === 'pagada' ? 'bg-emerald-500/20 text-emerald-400' : inv.status === 'vencida' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}>
                    {inv.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Activity Tab Component
function ActivityTab({ clientId }) {
  const { t, isSpanish } = useLanguage();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchActivity = useCallback(async () => {
    try {
      const response = await getClientActivity(clientId);
      setActivities(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [clientId, t]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  const getActivityIcon = (type) => {
    const icons = {
      project_created: { icon: Folder, color: 'text-[#3AB8D6]' },
      quotation_created: { icon: Receipt, color: 'text-[#E2B05E]' },
      quotation_accepted: { icon: CheckCircle, color: 'text-emerald-400' },
      invoice_created: { icon: Receipt, color: 'text-purple-400' },
      payment_received: { icon: CurrencyDollar, color: 'text-emerald-400' },
      task_completed: { icon: CheckCircle, color: 'text-[#3AB8D6]' },
    };
    return icons[type] || { icon: Pulse, color: 'text-[#9CA3AF]' };
  };

  if (loading) {
    return <div className="text-center py-8 text-[#9CA3AF]">{t('loading')}</div>;
  }

  if (activities.length === 0) {
    return (
      <div className="valhalla-card p-12 text-center">
        <Pulse className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" />
        <p className="text-[#9CA3AF]">{t('noData')}</p>
      </div>
    );
  }

  return (
    <div className="valhalla-card p-6">
      <h3 className="text-lg font-semibold text-[#F3F4F6] mb-6 flex items-center gap-2">
        <Pulse className="w-5 h-5 text-[#3AB8D6]" />
        {isSpanish ? 'Línea de Tiempo' : 'Activity Timeline'}
      </h3>
      <div className="relative">
        <div className="absolute left-6 top-0 bottom-0 w-px bg-[#242E42]"></div>
        <div className="space-y-6">
          {activities.map((activity, idx) => {
            const { icon: Icon, color } = getActivityIcon(activity.type);
            return (
              <div key={idx} className="relative flex gap-4 pl-3">
                <div className={`w-8 h-8 rounded-full bg-[#0B0E14] border border-[#242E42] flex items-center justify-center z-10 ${color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 pb-4">
                  <p className="text-[#F3F4F6] font-medium">{activity.title}</p>
                  <p className="text-sm text-[#9CA3AF]">{activity.description}</p>
                  <p className="text-xs text-[#6B7280] mt-1">{activity.date?.split('T')[0]}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Main Page Component
export function ClientDetailPage() {
  const { t, isSpanish } = useLanguage();
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await getClientDashboard(clientId);
      setDashboard(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  }, [clientId, navigate, t]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return (
    <Layout title={dashboard?.client?.name || t('clientDetails')}>
      <div className="space-y-6" data-testid="client-detail-page">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate('/clients')} className="text-[#9CA3AF] hover:text-[#F3F4F6]">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {isSpanish ? 'Volver a Clientes' : 'Back to Clients'}
        </Button>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#0B0E14] border border-[#242E42] flex-wrap h-auto p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#E2B05E] data-[state=active]:text-[#0B0E14]">
              <Buildings className="w-4 h-4 mr-2" />
              {isSpanish ? 'Resumen' : 'Overview'}
            </TabsTrigger>
            <TabsTrigger value="projects" className="data-[state=active]:bg-[#E2B05E] data-[state=active]:text-[#0B0E14]">
              <Folder className="w-4 h-4 mr-2" />
              {t('projects')}
            </TabsTrigger>
            <TabsTrigger value="team" className="data-[state=active]:bg-[#E2B05E] data-[state=active]:text-[#0B0E14]">
              <Users className="w-4 h-4 mr-2" />
              {t('team')}
            </TabsTrigger>
            <TabsTrigger value="hours" className="data-[state=active]:bg-[#E2B05E] data-[state=active]:text-[#0B0E14]">
              <Clock className="w-4 h-4 mr-2" />
              {t('hours')}
            </TabsTrigger>
            <TabsTrigger value="financials" className="data-[state=active]:bg-[#E2B05E] data-[state=active]:text-[#0B0E14]">
              <CurrencyDollar className="w-4 h-4 mr-2" />
              {isSpanish ? 'Finanzas' : 'Financials'}
            </TabsTrigger>
            <TabsTrigger value="activity" className="data-[state=active]:bg-[#E2B05E] data-[state=active]:text-[#0B0E14]">
              <Pulse className="w-4 h-4 mr-2" />
              {isSpanish ? 'Actividad' : 'Activity'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <OverviewTab dashboard={dashboard} loading={loading} />
          </TabsContent>
          <TabsContent value="projects" className="mt-6">
            <ProjectsTab clientId={clientId} />
          </TabsContent>
          <TabsContent value="team" className="mt-6">
            <TeamTab clientId={clientId} />
          </TabsContent>
          <TabsContent value="hours" className="mt-6">
            <HoursTab clientId={clientId} />
          </TabsContent>
          <TabsContent value="financials" className="mt-6">
            <FinancialsTab clientId={clientId} />
          </TabsContent>
          <TabsContent value="activity" className="mt-6">
            <ActivityTab clientId={clientId} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

export default ClientDetailPage;
